import React, { useState } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { 
  Shield, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  ChevronDown,
  ChevronUp,
  Info
} from "lucide-react";

interface CancellationProps {
  propertyData: PropertyToEdit | null;
}

const Cancellation: React.FC<CancellationProps> = ({ propertyData }) => {
  const [expandedSection, setExpandedSection] = useState<string | null>("cancellation");

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Sample house rules and safety info (you can replace with actual data from propertyData)
  const houseRules = [
    { id: 1, rule: "No smoking inside the property", allowed: false },
    { id: 2, rule: "Pets allowed with prior approval", allowed: true },
    { id: 3, rule: "Quiet hours: 10 PM - 8 AM", allowed: true },
    { id: 4, rule: "Guests must be registered", allowed: true },
    { id: 5, rule: "No parties or events", allowed: false },
  ];

  const safetyItems = [
    { id: 1, item: "Smoke detector", available: true },
    { id: 2, item: "Carbon monoxide detector", available: true },
    { id: 3, item: "Fire extinguisher", available: true },
    { id: 4, item: "First aid kit", available: true },
  ];

  return (
    <div className="listingSection__wrap w-full">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
            <Info size={24} className="text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Things to know
          </h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Important information about your stay
        </p>
      </div>

      <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent my-6" />

      {/* Sections Grid */}
      <div className="space-y-4">
        {/* Cancellation Policy */}
        <div className="border border-gray-200 dark:border-neutral-700 rounded-2xl overflow-hidden bg-white dark:bg-neutral-800 shadow-sm hover:shadow-md transition-shadow">
          <button
            onClick={() => toggleSection("cancellation")}
            className="w-full flex items-center justify-between p-5 hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <Shield size={20} className="text-red-600 dark:text-red-400" />
              </div>
              <div className="text-left">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                  Cancellation policy
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {propertyData?.cancellation_policy?.name || "Standard policy"}
                </p>
              </div>
            </div>
            {expandedSection === "cancellation" ? (
              <ChevronUp size={20} className="text-gray-400" />
            ) : (
              <ChevronDown size={20} className="text-gray-400" />
            )}
          </button>
          
          {expandedSection === "cancellation" && (
            <div className="px-5 pb-5 pt-2 border-t border-gray-100 dark:border-neutral-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {propertyData?.cancellation_policy?.description || 
                  "Free cancellation up to 48 hours before check-in. After that, cancel up to 24 hours before check-in and get a 50% refund of the nightly rate and the cleaning fee."}
              </p>
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-900 dark:text-amber-100">
                  <span className="font-semibold">Note:</span> Service fees are non-refundable. 
                  Review the full cancellation policy before booking.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* House Rules - HIDDEN */}
        <div className="hidden border border-gray-200 dark:border-neutral-700 rounded-2xl overflow-hidden bg-white dark:bg-neutral-800 shadow-sm hover:shadow-md transition-shadow">
          <button
            onClick={() => toggleSection("rules")}
            className="w-full flex items-center justify-between p-5 hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <Clock size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-left">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                  House rules
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Please follow these guidelines
                </p>
              </div>
            </div>
            {expandedSection === "rules" ? (
              <ChevronUp size={20} className="text-gray-400" />
            ) : (
              <ChevronDown size={20} className="text-gray-400" />
            )}
          </button>
          
          {expandedSection === "rules" && (
            <div className="px-5 pb-5 pt-2 border-t border-gray-100 dark:border-neutral-700">
              <div className="space-y-2.5">
                {houseRules.map((rule) => (
                  <div key={rule.id} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors">
                    {rule.allowed ? (
                      <CheckCircle size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                    )}
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {rule.rule}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Safety & Property - HIDDEN */}
        <div className="hidden border border-gray-200 dark:border-neutral-700 rounded-2xl overflow-hidden bg-white dark:bg-neutral-800 shadow-sm hover:shadow-md transition-shadow">
          <button
            onClick={() => toggleSection("safety")}
            className="w-full flex items-center justify-between p-5 hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <AlertCircle size={20} className="text-green-600 dark:text-green-400" />
              </div>
              <div className="text-left">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                  Safety & property
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Security features available
                </p>
              </div>
            </div>
            {expandedSection === "safety" ? (
              <ChevronUp size={20} className="text-gray-400" />
            ) : (
              <ChevronDown size={20} className="text-gray-400" />
            )}
          </button>
          
          {expandedSection === "safety" && (
            <div className="px-5 pb-5 pt-2 border-t border-gray-100 dark:border-neutral-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {safetyItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-gray-50 dark:bg-neutral-700/50">
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {item.item}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-xs text-green-900 dark:text-green-100">
                  <span className="font-semibold">Safety first:</span> All safety equipment is regularly inspected and maintained.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cancellation;